import React from 'react';
import { inject, observer } from 'mobx-react';
import { Badge, BadgeProps, Card, Flex, Group, Stack } from '@mantine/core';
import {
  IconArrowRight,
  IconCalendarDue,
  IconCalendarStats,
  IconRepeat,
} from '@tabler/icons-react';

export type IDataCardProps = {
  leftBadgeProps: BadgeProps;
  rightBadgeProps?: BadgeProps;
  recurringBadgeProps?: BadgeProps;
  recurringBadgeText?: string;
  planBadgeProps?: BadgeProps;
  planBadgeText?: string;
  leftBadgeText: string;
  rightBadgeText?: string;
  title: React.ReactNode;
  description: React.ReactNode;
  width: string | number;
  clickable?: boolean;
  onClick?: () => void;
  onDisabledClick?: () => void;
  additionalAction?: React.ReactNode;
  disabled?: boolean;
  readOnly?: boolean;
};

const DataCard: React.FC<IDataCardProps> = inject()(
  observer(
    ({
      leftBadgeProps,
      rightBadgeProps,
      recurringBadgeProps,
      recurringBadgeText,
      planBadgeProps,
      planBadgeText,
      leftBadgeText,
      rightBadgeText,
      title,
      description,
      width,
      clickable,
      onClick,
      onDisabledClick,
      additionalAction,
      disabled,
      readOnly,
    }) => {
      return (
        <Card
          withBorder
          miw={width}
          shadow={'none'}
          mb={'md'}
          p={'sm'}
          onClick={readOnly ? undefined : disabled ? onDisabledClick : onClick}
          style={{
            cursor: readOnly ? 'default' : disabled ? 'not-allowed' : 'pointer',
            opacity: disabled ? 0.6 : 1,
            backgroundColor: disabled
              ? 'var(--mantine-color-gray-0)'
              : undefined,
          }}
        >
          <Stack justify={'flex-end'} mb={'md'} gap={'xs'} w={'100%'}>
            <Group gap={'xs'} wrap={'wrap'} w={'100%'}>
              <Badge
                variant={'light'}
                size={'md'}
                {...leftBadgeProps}
                leftSection={<IconCalendarDue size={16} />}
              >
                {leftBadgeText}
              </Badge>
              {planBadgeText && (
                <Badge
                  variant={'light'}
                  size={'md'}
                  {...planBadgeProps}
                  leftSection={<IconCalendarStats size={16} />}
                >
                  {planBadgeText}
                </Badge>
              )}
              {recurringBadgeProps && (
                <Badge
                  variant={'light'}
                  size={'md'}
                  {...recurringBadgeProps}
                  leftSection={<IconRepeat size={16} />}
                >
                  {recurringBadgeText}
                </Badge>
              )}
              {rightBadgeText && (
                <Badge
                  variant={'light'}
                  size={'md'}
                  ml={'auto'}
                  {...rightBadgeProps}
                >
                  {rightBadgeText}
                </Badge>
              )}
            </Group>
            <Flex justify={'space-between'} w={'100%'} mt={'md'}>
              <Stack
                align={'start'}
                justify={'flex-start'}
                gap={'xs'}
                w={'100%'}
              >
                {title && title}
                {description && description}
              </Stack>
              <Flex align="center" gap="xs">
                {additionalAction}
                {clickable && (
                  <IconArrowRight
                    size={24}
                    color={
                      disabled
                        ? 'var(--mantine-color-gray-5)'
                        : 'var(--mantine-color-blue-7)'
                    }
                  />
                )}
              </Flex>
            </Flex>
          </Stack>
        </Card>
      );
    },
  ),
);

export default DataCard;
